import { useState } from "react";
import { Mail, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";

const STATUSES = ["NEW", "READ", "REPLIED", "ARCHIVED"];
const FILTERS = ["ALL", ...STATUSES];

const MessageItem = ({ m, onStatus, onDelete }) => (
  <li className="rounded-2xl border-2 border-line bg-surface p-5" data-testid={`message-item-${m.id}`}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <p className="font-bold">{m.name} · <a href={`mailto:${m.email}?subject=${encodeURIComponent(`Re: ${m.topic}`)}`} className="text-brand underline-offset-4 hover:underline" data-testid={`message-reply-${m.id}`}>{m.email}</a></p>
        <p className="text-sm text-ink-soft">{new Date(m.created_at).toLocaleString("en-GB")} · {m.topic}{m.organisation ? ` · ${m.organisation}` : ""}</p>
      </div>
      <div className="flex items-center gap-2">
        <label htmlFor={`ms-${m.id}`} className="sr-only">Status</label>
        <select id={`ms-${m.id}`} value={m.status} onChange={(e) => onStatus(m.id, e.target.value)} className="field-input !py-1.5 text-sm" data-testid={`message-status-${m.id}`}>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button type="button" onClick={() => onDelete(m.id)} className="btn btn-ghost btn-sm" aria-label={`Delete message from ${m.name}`} data-testid={`message-delete-${m.id}`}><Trash2 size={14} aria-hidden /></button>
      </div>
    </div>
    <p className="mt-3 whitespace-pre-wrap">{m.message}</p>
  </li>
);

export const Messages = ({ items, setItems }) => {
  const [filter, setFilter] = useState("ALL");
  const onStatus = async (id, status) => {
    try {
      await api.patch(`/admin/messages/${id}`, { status });
      setItems((xs) => xs.map((x) => (x.id === id ? { ...x, status } : x)));
    } catch (e) { toast.error(errorText(e)); }
  };
  const onDelete = async (id) => {
    if (!window.confirm("Delete this message permanently?")) return;
    try {
      await api.delete(`/admin/messages/${id}`);
      setItems((xs) => xs.filter((x) => x.id !== id));
    } catch (e) { toast.error(errorText(e)); }
  };
  const shown = filter === "ALL" ? items : items.filter((m) => m.status === filter);
  return (
    <div className="space-y-5" data-testid="admin-messages-tab">
      <div role="group" aria-label="Filter messages" className="flex flex-wrap gap-2">
        {FILTERS.map((s) => (
          <button key={s} type="button" aria-pressed={filter === s} onClick={() => setFilter(s)} className={`chip ${filter === s ? "bg-brand text-brand-fg" : ""}`} data-testid={`message-filter-${s.toLowerCase()}`}>
            {s} ({s === "ALL" ? items.length : items.filter((m) => m.status === s).length})
          </button>
        ))}
      </div>
      {shown.length ? <ul className="space-y-3">{shown.map((m) => <MessageItem key={m.id} m={m} onStatus={onStatus} onDelete={onDelete} />)}</ul>
        : <p className="flex items-center gap-2 rounded-2xl border-2 border-dashed border-line p-6 text-ink-soft" data-testid="messages-empty"><Mail size={18} aria-hidden /> No messages here yet.</p>}
    </div>
  );
};
